import { useEffect, useRef, useState } from 'react';
import { callable } from '../lib/callable';

const COUNTRY_CODES = [
  { code: '+91', flag: '🇮🇳' },
  { code: '+1', flag: '🇺🇸' },
  { code: '+44', flag: '🇬🇧' },
  { code: '+971', flag: '🇦🇪' },
  { code: '+65', flag: '🇸🇬' },
  { code: '+61', flag: '🇦🇺' },
  { code: '+977', flag: '🇳🇵' },
  { code: '+880', flag: '🇧🇩' },
];

const HANDBOOK_SHARE_URL = 'https://shreyaadieducation.in/handbook';

type Stage = 'form' | 'otp' | 'download';

function Spinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block', width: 16, height: 16, border: '2px solid rgba(255,255,255,0.5)',
        borderTopColor: '#fff', borderRadius: '50%', animation: 'hbSpin 0.6s linear infinite', verticalAlign: 'middle',
      }}
    />
  );
}

export function HandbookModal({ onClose }: { onClose: () => void }) {
  const [stage, setStage] = useState<Stage>('form');

  // form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [mobile, setMobile] = useState('');
  const [consent, setConsent] = useState(false);

  // otp / download
  const [requestId, setRequestId] = useState('');
  const [otp, setOtp] = useState('');
  const [signedUrl, setSignedUrl] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  // Resend cooldown countdown.
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (resendIn <= 0) return;
    timerRef.current = setInterval(() => setResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [resendIn]);

  async function sendCode() {
    setError(null);
    if (name.trim().length < 2) { setError('Please enter your name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Please enter a valid email.'); return; }
    if (!/^\d{10}$/.test(mobile.trim())) { setError('Mobile must be a 10-digit number.'); return; }
    if (!consent) { setError('Please agree to receive the handbook to continue.'); return; }
    setLoading(true);
    try {
      const call = (d: { name: string; email: string; mobile: string; countryCode: string; consentToMarketing: boolean }) =>
        callable<typeof d, { requestId: string }>('requestHandbookAccess', d);
      const res = await call({ name: name.trim(), email: email.trim().toLowerCase(), mobile: mobile.trim(), countryCode, consentToMarketing: consent });
      setRequestId(res.data.requestId);
      setStage('otp');
      setResendIn(30);
    } catch (e) {
      setError(friendly(e));
    } finally {
      setLoading(false);
    }
  }

  async function resendCode() {
    if (resendIn > 0 || loading) return;
    setError(null);
    setLoading(true);
    try {
      const call = (d: { name: string; email: string; mobile: string; countryCode: string; consentToMarketing: boolean }) =>
        callable<typeof d, { requestId: string }>('requestHandbookAccess', d);
      const res = await call({ name: name.trim(), email: email.trim().toLowerCase(), mobile: mobile.trim(), countryCode, consentToMarketing: consent });
      setRequestId(res.data.requestId);
      setOtp('');
      setResendIn(30);
    } catch (e) {
      setError(friendly(e));
    } finally {
      setLoading(false);
    }
  }

  async function verify() {
    setError(null);
    if (!/^\d{6}$/.test(otp.trim())) { setError('Enter the 6-digit code.'); return; }
    setLoading(true);
    try {
      const call = (d: { requestId: string; otp: string }) =>
        callable<typeof d, { signedUrl: string; expiresAt: string }>('verifyHandbookOtp', d);
      const res = await call({ requestId, otp: otp.trim() });
      setSignedUrl(res.data.signedUrl);
      setStage('download');
    } catch (e) {
      setError(friendly(e));
    } finally {
      setLoading(false);
    }
  }

  function onDownload() {
    window.open(signedUrl, '_blank', 'noopener,noreferrer');
    // Bonus: best-effort record of the download; don't block the user on it.
    callable<{ requestId: string }, { ok: true }>('markHandbookDownloaded', { requestId }).catch(() => {});
  }

  const waShare = `https://wa.me/?text=${encodeURIComponent(`Get the Free Career Handbook (2026 Edition) from Shrey Aadi Education: ${HANDBOOK_SHARE_URL}`)}`;

  return (
    <div onClick={onClose} role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000 }}>
      <style>{`@keyframes hbSpin { to { transform: rotate(360deg); } }
        @media (max-width: 600px) { .hb-modal { max-width: 100% !important; width: 100% !important; border-radius: 10px !important; } }`}</style>
      <div className="hb-modal" onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, padding: '2rem 1.75rem', maxWidth: 440, width: '100%', maxHeight: '90vh', overflowY: 'auto', position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <button onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: '0.5rem', right: '0.75rem', background: 'none', border: 'none', fontSize: '1.75rem', cursor: 'pointer', color: '#666', padding: '0.25rem 0.5rem', lineHeight: 1 }}>×</button>

        {stage === 'form' && (
          <>
            <h3 style={H3}>Get your free Career Handbook</h3>
            <p style={Sub}>65-page guide · 2026 Edition · ₹0</p>
            <Field label="Name">
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Your full name" style={Input} />
            </Field>
            <Field label="Email">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" style={Input} />
            </Field>
            <Field label="Mobile">
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} style={{ ...Input, width: 'auto', flex: '0 0 auto' }}>
                  {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
                </select>
                <input type="tel" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="9876543210" style={{ ...Input, flex: 1 }} />
              </div>
            </Field>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginTop: '1rem', fontSize: '0.82rem', color: '#444', lineHeight: 1.45 }}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: '0.15rem', flex: '0 0 auto' }} />
              <span>
                I agree to receive the Career Handbook and occasional updates from Shrey Aadi Education.
                I can unsubscribe anytime. See{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#1a4480' }}>Privacy Policy</a>.
              </span>
            </label>
            {error && <ErrorText>{error}</ErrorText>}
            <button onClick={sendCode} disabled={!consent} style={{ ...PrimaryBtn, ...(consent ? {} : { background: '#9db0c9', cursor: 'not-allowed' }) }}>{loading ? <Spinner /> : 'Send me the code'}</button>
            <p style={{ margin: '0.75rem 0 0', fontSize: '0.72rem', color: '#6b7280', lineHeight: 1.45 }}>
              Your email and phone are stored to deliver the handbook and send updates about new mentors
              and platform features. We never sell or share your data with third parties. Withdraw consent
              anytime by emailing <a href="mailto:privacy@shreyaadieducation.in" style={{ color: '#6b7280' }}>privacy@shreyaadieducation.in</a>.
            </p>
          </>
        )}

        {stage === 'otp' && (
          <>
            <h3 style={H3}>Enter the 6-digit code</h3>
            <p style={Sub}>Sent to {email}. Expires in 10 minutes.</p>
            <input
              type="text" inputMode="numeric" value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••••"
              style={{ ...Input, textAlign: 'center', fontSize: '1.6rem', letterSpacing: '0.5em', fontWeight: 700 }}
            />
            {error && <ErrorText>{error}</ErrorText>}
            <button onClick={verify} style={PrimaryBtn}>{loading ? <Spinner /> : 'Verify'}</button>
            <button
              onClick={resendCode}
              disabled={resendIn > 0}
              style={{ ...SecondaryBtn, cursor: resendIn > 0 ? 'not-allowed' : 'pointer', color: resendIn > 0 ? '#aaa' : '#1a4480' }}
            >
              {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
            </button>
          </>
        )}

        {stage === 'download' && (
          <>
            <h3 style={H3}>🎉 Here&apos;s your handbook</h3>
            <p style={Sub}>Valid for 1 hour. Save it to your device.</p>
            <button onClick={onDownload} style={{ ...PrimaryBtn, fontSize: '1.05rem' }}>Download PDF ↓</button>
            <a href={waShare} target="_blank" rel="noopener noreferrer" style={{ ...SecondaryBtn, display: 'block', textAlign: 'center', textDecoration: 'none' }}>
              Share with a friend
            </a>
            <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #eee', textAlign: 'center' }}>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Looking for a mentor for your prep? </span>
              <a href="/" style={{ color: '#1a4480', fontWeight: 600, fontSize: '0.9rem' }}>Browse mentors →</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Map HttpsError codes/messages to user-facing copy.
function friendly(e: unknown): string {
  const msg = (e as { message?: string })?.message ?? 'Something went wrong. Please try again.';
  return msg.replace(/^.*?\/.*?:\s*/, ''); // strip "functions/code:" prefix if present
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginTop: '1rem' }}>
      <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#444' }}>{label}</span>
      {children}
    </label>
  );
}

function ErrorText({ children }: { children: React.ReactNode }) {
  return <p style={{ margin: '0.6rem 0 0', color: '#b91c1c', fontSize: '0.85rem' }}>{children}</p>;
}

const H3: React.CSSProperties = { margin: 0, fontSize: '1.35rem', color: '#1a4480' };
const Sub: React.CSSProperties = { margin: '0.35rem 0 0.5rem', color: '#666', fontSize: '0.9rem' };
const Input: React.CSSProperties = { display: 'block', width: '100%', padding: '0.6rem', fontSize: '0.95rem', border: '1px solid #ccc', borderRadius: 6, marginTop: '0.35rem', boxSizing: 'border-box' };
const PrimaryBtn: React.CSSProperties = { width: '100%', marginTop: '1.25rem', padding: '0.75rem', background: '#1a4480', color: 'white', border: 'none', borderRadius: 6, fontSize: '1rem', fontWeight: 700, cursor: 'pointer', minHeight: 46 };
const SecondaryBtn: React.CSSProperties = { width: '100%', marginTop: '0.6rem', padding: '0.6rem', background: 'white', color: '#1a4480', border: '1px solid #1a4480', borderRadius: 6, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', boxSizing: 'border-box' };
